
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/providers/SettingsProvider";
import { Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { toast } from "sonner";

interface CalendarSettingsProps {
	isOpen: boolean;
	onClose: () => void;
}

const DAYS_OF_WEEK = [
	{ value: "0", label: "Sunday" },
	{ value: "1", label: "Monday" },
	{ value: "2", label: "Tuesday" },
	{ value: "3", label: "Wednesday" },
	{ value: "4", label: "Thursday" },
	{ value: "5", label: "Friday" },
	{ value: "6", label: "Saturday" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
	value: i.toString(),
	label: i.toString().padStart(2, '0') + ':00',
}));

const EVENT_DURATIONS = [
	{ value: "15", label: "15 minutes" },
	{ value: "30", label: "30 minutes" },
	{ value: "60", label: "1 hour" },
	{ value: "120", label: "2 hours" },
];

const EVENT_COLORS = [
	{ value: "#9b87f5", label: "Purple" },
	{ value: "#1EAEDB", label: "Blue" },
	{ value: "#F97316", label: "Orange" },
	{ value: "#D946EF", label: "Pink" },
	{ value: "#0EA5E9", label: "Sky" },
	{ value: "#8E9196", label: "Gray" },
];

export const CalendarSettings = ({ isOpen, onClose }: CalendarSettingsProps) => {
	const { 
		calendarSettings,
		updateCalendarSettings,
		isSaving
	} = useSettings();

	// Local state to track changes before saving
	const [localSettings, setLocalSettings] = useState(calendarSettings);

	// Reset local settings when modal opens
	useEffect(() => {
		setLocalSettings(calendarSettings);
	}, [isOpen, calendarSettings]);

	const handleWorkWeekDayToggle = (day: string) => {
		const currentDays = localSettings?.workWeekDays || [];
		const newDays = currentDays.includes(day)
			? currentDays.filter(d => d !== day)
			: [...currentDays, day].sort();
		
		setLocalSettings({
			...localSettings,
			workWeekDays: newDays
		});
	};

	const handleSave = async () => {
		try {
			await updateCalendarSettings(localSettings);
			toast.success("Calendar settings saved successfully");
			onClose();
		} catch (error) {
			console.error("Error saving settings:", error);
			toast.error("Failed to save settings");
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Settings className="h-5 w-5" />
						Calendar Settings
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-6 py-4">
					<div className="space-y-4">
						<div className="flex justify-between gap-4">
							<div className="flex-1 space-y-2">
								<Label>Work day starts</Label>
								<Select
									value={localSettings?.workDayStart?.toString()}
									onValueChange={(value) => setLocalSettings({
										...localSettings,
										workDayStart: parseInt(value)
									})}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select start time" />
									</SelectTrigger>
									<SelectContent>
										{HOURS.map((hour) => (
											<SelectItem key={hour.value} value={hour.value}>
												{hour.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							
							<div className="flex-1 space-y-2">
								<Label>Work day ends</Label>
								<Select
									value={localSettings?.workDayEnd?.toString()}
									onValueChange={(value) => setLocalSettings({
										...localSettings,
										workDayEnd: parseInt(value)
									})}
								>
									<SelectTrigger>
										<SelectValue placeholder="Select end time" />
									</SelectTrigger>
									<SelectContent>
										{HOURS.map((hour) => (
											<SelectItem key={hour.value} value={hour.value}>
												{hour.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						<div className="space-y-2">
							<Label>Work week days</Label>
							<div className="flex flex-wrap gap-2">
								{DAYS_OF_WEEK.map((day) => (
									<div key={day.value} className="flex items-center space-x-2">
										<Checkbox
											id={`day-${day.value}`}
											checked={localSettings?.workWeekDays?.includes(day.value)}
											onCheckedChange={() => handleWorkWeekDayToggle(day.value)}
										/>
										<label
											htmlFor={`day-${day.value}`}
											className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
										>
											{day.label}
										</label>
									</div>
								))}
							</div>
						</div>

						<div className="space-y-2">
							<Label>First day of week</Label>
							<Select
								value={localSettings?.firstDayOfWeek?.toString()}
								onValueChange={(value) => setLocalSettings({
									...localSettings,
									firstDayOfWeek: parseInt(value)
								})}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select first day" />
								</SelectTrigger>
								<SelectContent>
									{DAYS_OF_WEEK.map((day) => (
										<SelectItem key={day.value} value={day.value}>
											{day.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<Label>Default event duration</Label>
							<Select
								value={localSettings?.defaultEventDuration?.toString()}
								onValueChange={(value) => setLocalSettings({
									...localSettings,
									defaultEventDuration: parseInt(value)
								})}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select duration" />
								</SelectTrigger>
								<SelectContent>
									{EVENT_DURATIONS.map((duration) => (
										<SelectItem key={duration.value} value={duration.value}>
											{duration.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="space-y-2">
							<Label>Default event color</Label>
							<Select
								value={localSettings?.defaultEventColor || "#9b87f5"}
								onValueChange={(value) => setLocalSettings({
									...localSettings,
									defaultEventColor: value
								})}
							>
								<SelectTrigger>
									<SelectValue placeholder="Select color" />
								</SelectTrigger>
								<SelectContent>
									{EVENT_COLORS.map((color) => (
										<SelectItem key={color.value} value={color.value}>
											<div className="flex items-center gap-2">
												<div 
													className="w-4 h-4 rounded-full" 
													style={{ backgroundColor: color.value }}
												/>
												{color.label}
											</div>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="flex justify-end gap-2 pt-4">
							<Button variant="outline" onClick={onClose}>
								Cancel
							</Button>
							<Button onClick={handleSave} disabled={isSaving}>
								{isSaving ? "Saving..." : "Save changes"}
							</Button>
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};
